/*
 * @Author: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @Date: 2025-04-23 12:01:29
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-04-24 14:30:39
 */
import { useCallback } from 'react'
import { message } from 'antd'
import actionTypes from '../../../../constants/actionTypes'
import { capturePromiseException } from '../../../../utils/sentryUtils'

export function useAiClarity({
  state,
  setState,
  props,
  imgRef,
  originalPhotos,
  lastTaskCreationTime,
  currentTaskId,
  handlePhotoClearSA,
  handleReportSA
}) {
  // 创建照片对比视图
  const createComparisonView = useCallback(
    (clarityUrl: string) => {
      // 保存原始照片列表
      const originalPhotosValue = props.photos

      // 获取当前索引并保存，以便退出时恢复
      const { activeIndex } = state
      setState((prevState) => ({
        ...prevState,
        originalIndexBeforeComparison: activeIndex
      }))

      // 创建只包含原图和清晰图的新照片列表
      const currentPhoto = originalPhotosValue[activeIndex]
      const enhancedPhoto = {
        ...currentPhoto,
        largeURL: clarityUrl,
        smallURL: clarityUrl
      }
      const comparisonPhotos = [currentPhoto, enhancedPhoto]

      // 保存原始照片列表到实例
      originalPhotos.current = originalPhotosValue

      // 更新Redux中的照片列表
      props.dispatch({
        type: actionTypes.SET_PHOTOS,
        data: comparisonPhotos
      })

      // 将当前活动索引设置为清晰版本的图片（索引为1）
      setState((prevState) => ({
        ...prevState,
        aiProcessing: false,
        activeIndex: 1, // 第二张是清晰图，默认展示它
        isComparisonMode: true // 标记为对比模式
      }))

      message.success('照片已变清晰，可以对比效果')
    },
    [props.photos, state.activeIndex, setState, originalPhotos, props.dispatch]
  )

  // 使用Image对象检查图片是否存在
  const checkIfImageExists = useCallback((url: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => resolve(true)
      img.onerror = () => resolve(false)
      img.src = `${url}?t=${Date.now()}`
    })
  }, [])

  // 使用已存在的高清文件
  const applyExistingHdFile = useCallback(
    (clarityUrl: string) => {
      createComparisonView(clarityUrl)
    },
    [createComparisonView]
  )

  // 轮询任务状态函数 - 移到createHdGenerationTask之前
  const pollTaskStatus = useCallback(
    (taskId: string) => {
      const pollInterval = 1000

      const checkStatus = async () => {
        if (!currentTaskId.current) return

        try {
          const res = await props.aiClearQueryTask({
            taskID: taskId,
            taskType: 'ai_service_photo_clarity'
          })

          const taskStatus = res.commonTaskList[0]?.taskStatus
          const processOutput = res.commonTaskList[0]?.processOutput
          let clarityUrl = ''
          if (processOutput) {
            clarityUrl = JSON.parse(processOutput)?.url || ''
          }

          const { activeIndex } = state
          const photo = props.photos[activeIndex]
          const originalUrl = photo?.largeURL

          if (taskStatus === 3) {
            currentTaskId.current = null
            if (clarityUrl === originalUrl || !clarityUrl) {
              setState((prevState) => ({ ...prevState, aiProcessing: false }))
              message.info('该图片无法变清晰')
              return
            }

            if (imgRef.current) {
              imgRef.current.src = clarityUrl
            }

            createComparisonView(clarityUrl)
            handlePhotoClearSA('generateSuccess', true)
          } else if ([4, 5, 6].includes(taskStatus)) {
            setState((prevState) => ({
              ...prevState,
              aiProcessing: false,
              aiProcessingFailed: true
            }))
            handlePhotoClearSA('generateFail', true)
          } else if (taskStatus < 3) {
            setTimeout(checkStatus, pollInterval)
          }
        } catch (err) {
          currentTaskId.current = null
          setState((prevState) => ({ ...prevState, aiProcessing: false }))
          capturePromiseException(err)
        }
      }

      setTimeout(checkStatus, pollInterval)
    },
    [
      props,
      state,
      setState,
      imgRef,
      currentTaskId,
      createComparisonView,
      handlePhotoClearSA
    ]
  )

  // 创建清晰图生成任务 - 移到pollTaskStatus之后
  const createHdGenerationTask = useCallback(async () => {
    try {
      const { photos, userId, groupId } = props
      const { activeIndex } = state
      const photo = photos[activeIndex]
      const srcUrl = (photo?.largeURL || '').split('?')[0]
      const processInput = JSON.stringify({
        subtaskType: 0,
        url: srcUrl,
        groupID: groupId
      })

      // 记录任务创建时间
      lastTaskCreationTime.current = Date.now()
      const res = await props.aiClearCreateTask({
        userID: userId,
        groupID: groupId,
        processInput: [processInput],
        taskType: 'ai_service_photo_clarity'
      })

      // 保存任务ID
      currentTaskId.current = res.taskID
      // 开始轮询任务状态
      pollTaskStatus(currentTaskId.current || '')
    } catch (err) {
      setState((prevState) => ({
        ...prevState,
        aiProcessing: false,
        aiProcessingFailed: true
      }))
      capturePromiseException(err)
    }
  }, [
    props,
    state,
    setState,
    lastTaskCreationTime,
    currentTaskId,
    pollTaskStatus
  ])

  // 取消生成任务
  const handleCancelAiClear = useCallback(() => {
    // 如果有正在进行的任务，取消它
    if (currentTaskId.current) {
      props
        .aiClearCancelTask({
          taskID: currentTaskId.current,
          taskType: 'ai_service_photo_clarity'
        })
        .then(() => {
          // 取消成功
          message.info('已取消生成')
        })
        .catch((err) => {
          capturePromiseException(err)
        })

      // 重置任务ID
      currentTaskId.current = null

      handlePhotoClearSA('cancel')
    }

    // 更新状态，关闭蒙层
    setState((prevState) => ({ ...prevState, aiProcessing: false }))
  }, [props, setState, currentTaskId, handlePhotoClearSA])

  // 处理失败提示蒙层的确认按钮
  const handleFailureConfirm = useCallback(() => {
    // 关闭失败提示蒙层
    setState((prevState) => ({
      ...prevState,
      aiProcessingFailed: false
    }))
  }, [setState])

  // 处理重试按钮
  const handleRetry = useCallback(() => {
    handlePhotoClearSA('retry')
    const now = Date.now()
    const elapsed = now - (lastTaskCreationTime.current || 0)

    // 关闭失败提示蒙层
    setState((prevState) => ({
      ...prevState,
      aiProcessingFailed: false,
      aiProcessing: true // 重新显示处理中蒙层
    }))

    // 如果距离上次创建任务不到10秒，则继续查询原来的任务
    if (currentTaskId.current && elapsed < 10000) {
      pollTaskStatus(currentTaskId.current)
    } else {
      // 如果超过10秒或没有当前任务ID，则创建新任务
      createHdGenerationTask()
    }
  }, [
    handlePhotoClearSA,
    setState,
    lastTaskCreationTime,
    currentTaskId,
    pollTaskStatus,
    createHdGenerationTask
  ])

  // HD显示处理
  const handleHdDisplay = useCallback(async () => {
    const { photos } = props
    const { activeIndex } = state
    const photo = photos[activeIndex]

    setState((prevState) => ({ ...prevState, aiProcessing: true }))
    handleReportSA('photoBeClearClick')

    const originalUrl = photo?.file || ''
    const fileName = originalUrl.split('/').pop()?.split('.')[0] || ''
    const clarityUrl = `https://static.xhey.top/ai_xhey_cv/ai_service_photo_clarity/${fileName}-clarity.jpg`

    try {
      const exists = await checkIfImageExists(clarityUrl)
      if (exists) {
        if (clarityUrl === originalUrl) {
          message.info('该图片无法变清晰')
          setState((prevState) => ({ ...prevState, aiProcessing: false }))
          return
        }
        applyExistingHdFile(clarityUrl)
      } else {
        createHdGenerationTask()
      }
    } catch (err) {
      setState((prevState) => ({ ...prevState, aiProcessing: false }))
      capturePromiseException(err)
    }
  }, [
    props,
    state,
    setState,
    handleReportSA,
    checkIfImageExists,
    applyExistingHdFile,
    createHdGenerationTask
  ])

  return {
    createComparisonView,
    checkIfImageExists,
    applyExistingHdFile,
    createHdGenerationTask,
    pollTaskStatus,
    handleCancelAiClear,
    handleFailureConfirm,
    handleRetry,
    handleHdDisplay
  }
}
